import { useEffect, useRef, useState, type ReactNode } from 'react';
import { stampa } from '../../api/stampa';
import type { PossibilitaStampa } from '../../api/stampa';

/*  Come si sta stampando, per chi deve stampare allo stesso modo: l'offerta che parte
    per mail. Gli stessi nomi del contratto `MailOfferta`.  */
export interface ComeStampare {
  mostraPrezzi: boolean;
  prezziSoloSuiTotali: boolean;
  mostraDettaglioArticoli: boolean;
  mostraRedattore: boolean;
  scavalcaPrezziDoppi: boolean;
}

/*  L'offerta da mandare: la si compone, la si guarda, e da qui la si manda.

    L'invio non e' roba di questa scheda — arriva da fuori nella prop `invio` — ma il
    suo bottone sta qui, di fianco a quelli che scaricano: sono un gesto solo, e mandare
    un documento senza averlo aperto e' il modo in cui esce un'offerta sbagliata.

    I due interruttori sono quelli della riunione — «mostra i prezzi», «mostra il
    dettaglio degli articoli» — ed è per questo che il modello è uno solo invece di
    cinque file che divergono al primo ritocco. Senza il dettaglio articoli restano i
    capitoli: «il cliente finale deve vedere solo la testa», la distinta serve a noi per
    fare gli ordini.

    Il PDF compare solo se sulla macchina dell'API c'è LibreOffice. Un bottone che dà
    «manca LibreOffice» manderebbe a chiamare noi — chi lo preme non ha modo di rimediare
    — mentre il .docx si può sempre esportare da Word, e infatti resta.

    I segnaposto sconosciuti si mostrano dopo, non prima: sono quelli che il modello
    chiede e che nessuno pubblica, e restano scritti nel documento. Vederli qui vuol dire
    accorgersi di un errore di battitura nel modello adesso, invece che dal cliente.

    L'ANTEPRIMA, chiesta in riunione al punto 7. Il PDF si guarda qui dentro prima di
    decidere cosa farne, e quello che si scarica poi sono ESATTAMENTE i byte che si sono
    guardati: non una seconda composizione, che potrebbe uscire diversa perche' nel
    frattempo qualcuno ha toccato il preventivo o il modello. Costa anche una conversione
    in meno — LibreOffice ci mette un paio di secondi.

    Toccare uno dei due interruttori butta via l'anteprima: quello che si sta guardando
    non e' piu' quello che uscirebbe, e un documento vecchio sotto interruttori nuovi e'
    il modo piu' rapido di mandare al cliente l'offerta con i prezzi che non doveva
    vedere.
*/
export function StampaOfferta({
  preventivoId,
  prezziDoppi = [],
  invio,
}: {
  preventivoId: string;
  /*  I codici che nell'offerta escono a prezzi diversi. Finche' ce n'e' uno la stampa
      e' bloccata — chiesto il 01/10/2026 — e si sblocca solo con un gesto apposta.

      La regola vera sta sul server, che rifiuta di comporre: questa copia serve a dirlo
      PRIMA di provarci, perche' un bottone che si lascia premere e poi da' un errore
      rosso e' peggio di un bottone spento che spiega perche'.  */
  prezziDoppi?: readonly string[];
  /*  Il comando che manda l'offerta al cliente, messo qui di fianco a quelli che la
      scaricano — chiesto il 15/09/2026. Arriva come nodo e non come funzione: questa
      scheda compone documenti e non sa niente di stati e permessi, e deve restare così.
      Un preventivo già uscito lo ristampa e basta, quindi la prop è facoltativa.  */
  /*  Dal 15/10/2026 puo' essere anche una funzione che riceve COME si sta stampando:
      l'offerta che parte per mail deve essere stampata come quella che si e' appena
      guardata, e le scelte stanno qui.  */
  invio?: ReactNode | ((come: ComeStampare) => ReactNode);
}) {
  const [possibilita, setPossibilita] = useState<PossibilitaStampa | null>(null);
  /*  TRE MODI DI MOSTRARE I PREZZI e non piu' due (15/10/2026): tutti, solo sui totali
      — il dettaglio degli articoli senza importi, il totale in fondo a ogni capitolo e
      il riepilogo — oppure nessuno. Il secondo e' quello che Galizzi ha chiesto in
      riunione: «senza questi, con solo questo totale».  */
  const [modoPrezzi, setModoPrezzi] = useState<'tutti' | 'totali' | 'nessuno'>('tutti');
  const prezzi = modoPrezzi !== 'nessuno';
  const soloTotali = modoPrezzi === 'totali';
  const [articoli, setArticoli] = useState(true);
  const [redattore, setRedattore] = useState(true);
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const [sconosciuti, setSconosciuti] = useState<string[] | null>(null);
  const [scavalco, setScavalco] = useState(false);

  //  Il blocco cade quando le righe si allineano, non solo quando qualcuno lo scavalca:
  //  sistemato il prezzo, i bottoni tornano accesi da soli.
  const bloccata = prezziDoppi.length > 0 && !scavalco;
  const [anteprima, setAnteprima] = useState<{
    indirizzo: string;
    nomeFile: string;
    contenuto: Blob;
  } | null>(null);

  /*  L'indirizzo temporaneo del blob va revocato quando non serve piu', o il PDF resta
      in memoria finche' non si chiude la scheda. Si tiene in un riferimento perche' va
      revocato anche quando il componente sparisce, e li' lo stato non si legge.  */
  const daRevocare = useRef<string | null>(null);

  const buttaViaLAnteprima = () => {
    if (daRevocare.current !== null) URL.revokeObjectURL(daRevocare.current);
    daRevocare.current = null;
    setAnteprima(null);
  };

  useEffect(() => () => {
    if (daRevocare.current !== null) URL.revokeObjectURL(daRevocare.current);
  }, []);

  //  Un preventivo diverso e' un documento diverso: l'anteprima di prima non c'entra piu'.
  useEffect(() => buttaViaLAnteprima, [preventivoId]);

  useEffect(() => {
    stampa
      .possibilita()
      .then(setPossibilita)
      .catch(() => setPossibilita({ pdf: false }));
  }, []);

  const componi = (formato: 'pdf' | 'docx') => {
    setInCorso(true);
    setErrore(null);
    setSconosciuti(null);

    stampa
      .preventivo(preventivoId, { formato, prezzi, soloTotali, articoli, redattore, scavalco })
      .then(setSconosciuti)
      .catch((e: Error) => setErrore(e.message))
      .finally(() => setInCorso(false));
  };

  const guarda = () => {
    setInCorso(true);
    setErrore(null);
    setSconosciuti(null);
    buttaViaLAnteprima();

    stampa
      .componi(preventivoId, {
        formato: 'pdf',
        prezzi,
        soloTotali,
        articoli,
        redattore,
        scavalco,
      })
      .then((file) => {
        const indirizzo = URL.createObjectURL(file.contenuto);
        daRevocare.current = indirizzo;
        setAnteprima({ indirizzo, nomeFile: file.nomeFile, contenuto: file.contenuto });
        setSconosciuti(file.sconosciuti);
      })
      .catch((e: Error) => setErrore(e.message))
      .finally(() => setInCorso(false));
  };

  //  Cambiare cosa si stampa rende vecchia l'anteprima: si butta invece di lasciarla li'.
  const cambia = (applica: () => void) => {
    buttaViaLAnteprima();
    setSconosciuti(null);
    applica();
  };

  return (
    <section className="stampa-offerta">
      <h2>Stampa</h2>

      <div className="interruttori">
        <label className="scelta-prezzi">
          Prezzi{' '}
          <select
            value={modoPrezzi}
            onChange={(e) =>
              cambia(() => setModoPrezzi(e.target.value as 'tutti' | 'totali' | 'nessuno'))
            }
          >
            <option value="tutti">tutti, voce per voce</option>
            <option value="totali">solo i totali dei capitoli e il riepilogo</option>
            <option value="nessuno">nessuno</option>
          </select>
        </label>
        <label>
          <input
            type="checkbox"
            checked={articoli}
            onChange={(e) => cambia(() => setArticoli(e.target.checked))}
          />
          Mostra il dettaglio degli articoli
        </label>
        {/*  Chiesto il 21/09/2026: un'offerta la scrive uno e la manda un altro, e il
             nome sulla firma dev'essere quello giusto — o nessuno.  */}
        <label>
          <input
            type="checkbox"
            checked={redattore}
            onChange={(e) => cambia(() => setRedattore(e.target.checked))}
          />
          Mostra chi ha scritto l’offerta
        </label>
      </div>

      {bloccata && (
        <div className="nota stampa-bloccata">
          <p>
            <strong>Stampa bloccata.</strong>{' '}
            {prezziDoppi.length === 1
              ? 'Un articolo compare a prezzi diversi nella stessa offerta:'
              : `${prezziDoppi.length} articoli compaiono a prezzi diversi nella stessa offerta:`}{' '}
            <em className="chiave">{prezziDoppi.join(', ')}</em>.
          </p>
          <p className="dettaglio">
            Si allineano le righe qui sopra, oppure si stampa lo stesso — pu&ograve; essere
            giusto, quantit&agrave; diverse o un prezzo concordato. Di chi lo decide resta
            traccia nel registro delle modifiche.
          </p>
          <button type="button" onClick={() => setScavalco(true)}>
            Stampo lo stesso
          </button>
        </div>
      )}

      {scavalco && prezziDoppi.length > 0 && (
        <p className="nota">
          Blocco scavalcato: l&rsquo;offerta si stampa con i prezzi doppi, e resta scritto
          nel registro.
        </p>
      )}

      <div className="comandi">
        {/*  Guardare prima di scaricare: chiesto in riunione, ed e' anche l'ordine in cui
             si lavora. Il bottone principale e' questo e non «scarica», perche' un file
             sceso nella cartella dei download e' un file che qualcuno apre, guarda e
             cancella — mentre qui si guarda e basta.  */}
        {possibilita?.pdf && anteprima === null && (
          <button type="button" className="primario" disabled={inCorso || bloccata} onClick={guarda}>
            {inCorso ? 'Composizione…' : 'Guarda l’offerta'}
          </button>
        )}

        {/*  A anteprima aperta il salvataggio non ricompone niente: scende il blob che si
             sta guardando, quindi quello che il cliente riceve e' quello che si e' visto.  */}
        {anteprima !== null && (
          <button
            type="button"
            className="primario"
            disabled={inCorso || bloccata}
            onClick={() => stampa.salva(anteprima.contenuto, anteprima.nomeFile)}
          >
            Scarica questo PDF
          </button>
        )}

        <button type="button" disabled={inCorso || bloccata} onClick={() => componi('docx')}>
          Scarica il Word
        </button>
        {typeof invio === 'function'
          ? invio({
              mostraPrezzi: prezzi,
              prezziSoloSuiTotali: soloTotali,
              mostraDettaglioArticoli: articoli,
              mostraRedattore: redattore,
              scavalcaPrezziDoppi: scavalco,
            })
          : invio}
      </div>

      {anteprima !== null && (
        <figure className="anteprima-stampa">
          <iframe src={anteprima.indirizzo} title={`Anteprima di ${anteprima.nomeFile}`} />
          <figcaption>
            {anteprima.nomeFile} · quello che scarichi e mandi è esattamente questo
            <button type="button" className="rimuovi" onClick={buttaViaLAnteprima}>
              chiudi
            </button>
          </figcaption>
        </figure>
      )}

      {possibilita !== null && !possibilita.pdf && (
        <p className="nota">
          Il PDF non si può fare su questa macchina: manca LibreOffice sul server. Il Word
          si scarica lo stesso e si esporta in PDF da lì.
        </p>
      )}

      {errore !== null && <p className="errore">{errore}</p>}

      {sconosciuti !== null && sconosciuti.length > 0 && (
        <p className="errore">
          Il modello chiede dei segnaposto che non esistono, e sono rimasti scritti nel
          documento: {sconosciuti.join(', ')}. Quasi sempre è un nome battuto male — l'elenco
          di quelli buoni è nella schermata dei testi dell'offerta.
        </p>
      )}

      {sconosciuti !== null && sconosciuti.length === 0 && (
        <p className="nota">Composto con il modello attivo, senza segnaposto rimasti a vista.</p>
      )}
    </section>
  );
}
